import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, type GridSpec, conditions, gridBodyRange, seededRandom } from "../spec";

const SHEET = "AG Inmuebles";
const TABLE = "Inmuebles";

const CIUDADES = ["Monterrey", "Guadalajara", "CDMX", "Querétaro"] as const;
const TIPOS = ["Casa", "Departamento"] as const;
// Precio base por m² de cada ciudad.
const M2: Record<(typeof CIUDADES)[number], number> = {
  Monterrey: 28000,
  Guadalajara: 24000,
  CDMX: 42000,
  Querétaro: 21000,
};

const rnd = seededRandom(8812);
// ID, Ciudad, Tipo, Recámaras, m², Precio, Días en mercado, Estado
const ROWS = Array.from({ length: 48 }, (_, i) => {
  const ciudad = CIUDADES[i % CIUDADES.length];
  // Querétaro solo tiene casas: la tabla de doble entrada tendrá un hueco.
  const tipo = ciudad === "Querétaro" ? "Casa" : rnd.pick(TIPOS);
  const recamaras = tipo === "Casa" ? rnd.int(2, 5) : rnd.int(1, 3);
  const m2 = tipo === "Casa" ? rnd.int(110, 320) : rnd.int(45, 140);
  const precio = Math.round((m2 * M2[ciudad] * rnd.int(85, 125)) / 100 / 10000) * 10000;
  return [
    `INM-${501 + i}`,
    ciudad,
    tipo,
    recamaras,
    m2,
    precio,
    rnd.int(7, 240),
    rnd.next() < 0.55 ? "Vendido" : "Disponible",
  ] as [string, string, string, number, number, number, number, string];
});

type Row = (typeof ROWS)[number];
const avg = (values: number[]) => values.reduce((a, b) => a + b, 0) / values.length;
const precios = (filter: (r: Row) => boolean) => ROWS.filter(filter).map((r) => r[5]);

const IN = {
  ciudad: "Monterrey",
  tipo: "Departamento",
  min: 3000000,
  max: 6000000,
  tipoSinDatos: "Terreno",
};

const GRID: GridSpec = {
  anchor: "J20",
  corner: "Ciudad \\ Tipo",
  rowHeaders: [...CIUDADES],
  colHeaders: [...TIPOS],
  format: "$#,##0",
};

export const spec: FuncionProjectSpec = {
  projectId: "funciones-promediosiconjunto",
  sheets: [
    {
      name: SHEET,
      title: "Análisis de precios inmobiliarios",
      subtitle: "Inventario de una inmobiliaria en cuatro ciudades — responde en las celdas amarillas",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["ID", "Ciudad", "Tipo", "Recámaras", "m²", "Precio", "Días en mercado", "Estado"],
          rows: ROWS,
          formats: { Precio: "$#,##0" },
        },
      ],
      grids: [GRID],
      inputs: [
        { address: "K4", label: "Ciudad", value: IN.ciudad },
        { address: "K5", label: "Tipo", value: IN.tipo },
        { address: "K6", label: "Precio mínimo", value: IN.min, format: "$#,##0" },
        { address: "K7", label: "Precio máximo", value: IN.max, format: "$#,##0" },
        { address: "K8", label: "Tipo a consultar", value: IN.tipoSinDatos },
      ],
      answers: [
        { address: "K11", label: "Tarea 1 · Precio promedio ciudad", format: "$#,##0" },
        { address: "K12", label: "Tarea 2 · Ciudad y tipo", format: "$#,##0" },
        { address: "K13", label: "Tarea 3 · Casas 3+ recámaras", format: "$#,##0" },
        { address: "K14", label: "Tarea 4 · Días para vender", format: "0.0" },
        { address: "K15", label: "Tarea 5 · Tipo a consultar", format: "$#,##0" },
        { address: "K17", label: "Tarea 7 · Rango de precios", format: "$#,##0" },
      ],
    },
  ],
  checks: {
    1: {
      kind: "cell", sheet: SHEET, address: "K11", fns: ["AVERAGEIFS"],
      expected: avg(precios((r) => r[1] === IN.ciudad)),
    },
    2: {
      kind: "cell", sheet: SHEET, address: "K12", fns: ["AVERAGEIFS"],
      expected: avg(precios((r) => r[1] === IN.ciudad && r[2] === IN.tipo)),
    },
    3: {
      kind: "cell", sheet: SHEET, address: "K13", fns: ["AVERAGEIFS"],
      expected: avg(precios((r) => r[2] === "Casa" && r[3] >= 3)),
    },
    4: {
      kind: "cell", sheet: SHEET, address: "K14", fns: ["AVERAGEIFS"],
      expected: avg(
        ROWS.filter((r) => r[7] === "Vendido" && r[5] >= IN.min && r[5] <= IN.max).map((r) => r[6])
      ),
    },
    5: {
      kind: "cell", sheet: SHEET, address: "K15", fns: ["AVERAGEIFS", "IFERROR"],
      expected: "Sin datos",
    },
    6: {
      kind: "block", sheet: SHEET, address: gridBodyRange(GRID), fns: ["AVERAGEIFS", "IFERROR"],
      expected: CIUDADES.map((c) =>
        TIPOS.map((t) => {
          const list = precios((r) => r[1] === c && r[2] === t);
          return list.length ? avg(list) : "Sin datos";
        })
      ),
    },
    7: {
      kind: "cell", sheet: SHEET, address: "K17", fns: ["MAXIFS", "MINIFS"],
      expected:
        Math.max(...precios((r) => r[1] === IN.ciudad)) - Math.min(...precios((r) => r[1] === IN.ciudad)),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;
const LAST = 4 + ROWS.length;
const GRID_FORMULA = `=SI.ERROR(PROMEDIO.SI.CONJUNTO($F$5:$F$${LAST},$B$5:$B$${LAST},$J21,$C$5:$C$${LAST},K$20),"Sin datos")`;

export const project: ProjectDefinition = {
  id: "funciones-promediosiconjunto",
  title: "PROMEDIO.SI.CONJUNTO: Precios inmobiliarios",
  subtitle: "Promedios que solo miran las filas que importan",
  description:
    "Trabajas en una inmobiliaria con propiedades en cuatro ciudades. Los asesores preguntan todo el tiempo: ¿cuánto cuesta en promedio un departamento en Monterrey? ¿Cuánto tarda en venderse una propiedad de 3 a 6 millones?\n\nPROMEDIO.SI.CONJUNTO tiene la misma estructura que SUMAR.SI.CONJUNTO, pero si ninguna fila cumple las condiciones devuelve #DIV/0!. Aprenderás a controlar ese caso.",
  topics: [
    "Promedio con uno y varios criterios",
    "Promediar una columna distinta a la del criterio",
    "#DIV/0! cuando nada coincide, con SI.ERROR",
    "Tabla de doble entrada de promedios",
    "MAX.SI.CONJUNTO y MIN.SI.CONJUNTO",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Precio promedio por ciudad",
      description:
        "En la hoja <strong>AG Inmuebles</strong>, celda <strong>K11</strong>: calcula el <strong>Precio</strong> promedio de las propiedades de la ciudad escrita en <strong>K4</strong>.",
      solution: [
        `Selecciona ${F("K11")}.`,
        `Igual que en SUMAR.SI.CONJUNTO: primero el rango a promediar, luego pares rango–criterio.`,
        `Escribe ${F("=PROMEDIO.SI.CONJUNTO(Inmuebles[Precio],Inmuebles[Ciudad],K4)")}.`,
      ],
      expectedResult: "Precio promedio en Monterrey",
      finalFormula: "=PROMEDIO.SI.CONJUNTO(Inmuebles[Precio],Inmuebles[Ciudad],K4)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Ciudad y tipo",
      description:
        "En <strong>K12</strong>: precio promedio de las propiedades de la ciudad de <strong>K4</strong> <em>y</em> el tipo de <strong>K5</strong>.",
      solution: [
        `Selecciona ${F("K12")}.`,
        `Escribe ${F("=PROMEDIO.SI.CONJUNTO(Inmuebles[Precio],Inmuebles[Ciudad],K4,Inmuebles[Tipo],K5)")}.`,
      ],
      expectedResult: "Precio promedio de un departamento en Monterrey",
      finalFormula: "=PROMEDIO.SI.CONJUNTO(Inmuebles[Precio],Inmuebles[Ciudad],K4,Inmuebles[Tipo],K5)",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Casas familiares",
      description: conditions("En <strong>K13</strong>: precio promedio de las propiedades que cumplan:", [
        "Tipo <strong>Casa</strong>",
        "<strong>3 recámaras o más</strong>",
      ]),
      solution: [
        `Selecciona ${F("K13")}.`,
        `Escribe ${F('=PROMEDIO.SI.CONJUNTO(Inmuebles[Precio],Inmuebles[Tipo],"Casa",Inmuebles[Recámaras],">=3")')}.`,
      ],
      expectedResult: "Precio promedio de casas de 3+ recámaras",
      finalFormula: '=PROMEDIO.SI.CONJUNTO(Inmuebles[Precio],Inmuebles[Tipo],"Casa",Inmuebles[Recámaras],">=3")',
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: ¿Cuánto tarda en venderse?",
      description: conditions(
        "En <strong>K14</strong>: promedio de <strong>Días en mercado</strong> (no del precio) de las propiedades que cumplan:",
        [
          "Estado <strong>Vendido</strong>",
          "Precio <strong>desde</strong> el de <strong>K6</strong>",
          "Precio <strong>hasta</strong> el de <strong>K7</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("K14")}.`,
        "El rango a promediar puede ser una columna distinta a las de los criterios: aquí promedias días, pero filtras por precio.",
        `Escribe ${F('=PROMEDIO.SI.CONJUNTO(Inmuebles[Días en mercado],Inmuebles[Estado],"Vendido",Inmuebles[Precio],">="&K6,Inmuebles[Precio],"<="&K7)')}.`,
      ],
      expectedResult: "Días promedio para vender entre 3 y 6 millones",
      finalFormula:
        '=PROMEDIO.SI.CONJUNTO(Inmuebles[Días en mercado],Inmuebles[Estado],"Vendido",Inmuebles[Precio],">="&K6,Inmuebles[Precio],"<="&K7)',
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Cuando no hay datos",
      description:
        "En <strong>K15</strong>: precio promedio de las propiedades de la ciudad de <strong>K4</strong> y el tipo de <strong>K8</strong>. No hay ningún \"Terreno\", así que el promedio daría <strong>#DIV/0!</strong>: muestra <strong>Sin datos</strong> en su lugar.",
      solution: [
        `Selecciona ${F("K15")}.`,
        "Si ninguna fila cumple, PROMEDIO.SI.CONJUNTO intenta dividir entre 0. SI.ERROR atrapa ese error.",
        `Escribe ${F('=SI.ERROR(PROMEDIO.SI.CONJUNTO(Inmuebles[Precio],Inmuebles[Ciudad],K4,Inmuebles[Tipo],K8),"Sin datos")')}.`,
      ],
      expectedResult: "Sin datos",
      finalFormula:
        '=SI.ERROR(PROMEDIO.SI.CONJUNTO(Inmuebles[Precio],Inmuebles[Ciudad],K4,Inmuebles[Tipo],K8),"Sin datos")',
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Tabla Ciudad × Tipo",
      description: conditions(
        "Llena <strong>K21:L24</strong> con el precio promedio de cada ciudad (filas) y tipo (columnas), con <strong>una sola fórmula</strong> arrastrada:",
        [
          "Ciudad desde la columna <strong>J</strong> → <strong>$J21</strong>",
          "Tipo desde la fila <strong>20</strong> → <strong>K$20</strong>",
          "Querétaro no tiene departamentos → esa celda debe mostrar <strong>Sin datos</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("K21")}.`,
        `Escribe ${F(GRID_FORMULA)}.`,
        `Arrastra hasta ${F("L21")} y luego hacia abajo hasta la fila ${F("24")}.`,
      ],
      expectedResult: "8 promedios, uno con Sin datos",
      finalFormula: GRID_FORMULA,
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Rango de precios",
      description: conditions(
        "En <strong>K17</strong>: calcula la diferencia entre la propiedad <strong>más cara</strong> y la <strong>más barata</strong> de la ciudad de <strong>K4</strong>.",
        [
          "<strong>MAX.SI.CONJUNTO</strong> → el precio más alto de esa ciudad",
          "<strong>MIN.SI.CONJUNTO</strong> → el precio más bajo de esa ciudad",
        ],
        "Resta uno menos el otro."
      ),
      solution: [
        `Selecciona ${F("K17")}.`,
        "MAX.SI.CONJUNTO y MIN.SI.CONJUNTO tienen exactamente la misma estructura que PROMEDIO.SI.CONJUNTO.",
        `Escribe ${F("=MAX.SI.CONJUNTO(Inmuebles[Precio],Inmuebles[Ciudad],K4)-MIN.SI.CONJUNTO(Inmuebles[Precio],Inmuebles[Ciudad],K4)")}.`,
      ],
      expectedResult: "Diferencia entre la más cara y la más barata de Monterrey",
      finalFormula:
        "=MAX.SI.CONJUNTO(Inmuebles[Precio],Inmuebles[Ciudad],K4)-MIN.SI.CONJUNTO(Inmuebles[Precio],Inmuebles[Ciudad],K4)",
    },
  ],
};
